import type { Sort, TableColumn } from '@library/types/utils';
import { Button } from '@library/components/buttons/Button';
import { SortIndicator } from '@library/components/SortIndicator';
import { cn as classNames } from '@library/component-utils/cn';
import { getSortDirection } from '@library/component-utils/table';

export interface TableHeadersProps<T extends string> {
  className?: string;
  children?: React.ReactNode;
  headers: Array<TableColumn>;
  sort?: Sort<T>;
  onSort?: (sort: Sort<T>) => void;
  buttonClassName?: string;
}

export const TableHeader = <T extends string>({
  className = 'min-w-[1020px]',
  children = undefined,
  headers,
  sort = undefined,
  onSort = undefined,
  buttonClassName = ''
}: TableHeadersProps<T>) => {
  const sortColumn = (column: T, sortEnabled: boolean) => {
    if (!sort || !onSort || !sortEnabled) return;

    const newSortDirection = getSortDirection({ column, sort });
    onSort({
      sortBy: newSortDirection === 'none' ? '' : column,
      sortDirection: newSortDirection
    });
  };

  return (
    <div
      className={classNames(
        'bg-soFresh-110 text-soClean-130 border-soFresh-140 sticky top-0 flex items-center rounded-t-md text-xs font-semibold',
        className
      )}
    >
      {headers.map(({ column, icon, label, sortEnabled, width }) => {
        const isSortEnabled = sortEnabled && sort;
        const isSortColumn = sort?.sortBy === column;

        return (
          <Button
            key={label}
            className={classNames(
              'group flex h-12 items-center px-3 text-left',
              width ?? 'flex-1',
              isSortEnabled ? 'cursor-pointer' : 'cursor-default',
              isSortColumn ? 'bg-soFresh-120' : '',
              buttonClassName
            )}
            data-testid={`Col-${label}`}
            onClick={() => sortColumn(column as T, sortEnabled)}
          >
            {column === 'checkbox' && children}
            {icon && (
              <img className="size-6" alt={`${label} icon`} src={icon} />
            )}
            {!icon && <span>{label}</span>}
            {isSortEnabled && (
              <SortIndicator
                className={classNames(
                  'ml-auto',
                  !isSortColumn ? 'hidden group-hover:block' : ''
                )}
                column={column}
                sort={sort}
              />
            )}
          </Button>
        );
      })}
    </div>
  );
};
